import axios from 'axios';
import {
  createSlice,
  createAsyncThunk,
  createEntityAdapter,
  createDraftSafeSelector,
} from '@reduxjs/toolkit';

import {
  GET_COMPANY_LIST,
  GET_COMPANY_BY_ID,
  GET_CLIENTS,
  POST_COMPANY,
  GET_MERCHANT_CATEGORY_CODE,
  POST_HOP_URL,
  POST_UPDATE_KYC_STATUS,
  POST_COMPANY_SELECT,
  GET_COMPANIES,
  POST_COMPANY_CREATE,
  GET_USER_CLIENTS,
} from 'services/api';
import { flatCompany, rejectValue } from 'redux/helpers';
import {
  COMPANY,
  COMPANY_ALL,
  COMPANY_LIST,
  COMPANY_SEARCH,
  COMPANY_GET_BY_ID,
  CREATE_COMPANY_AND_STORE,
  MERCHANT_CATEGORY_CODE,
  GENERATE_HOP_URL,
  CLIENT_ALL,
  UPDATE_KYC_STATUS,
  COMPANY_SELECT,
  COMPANY_ALL_INIT,
  COMPANY_CREATE_ACTION,
  USER_CLIENTS,
} from './types';
import { newCompany as TEXT } from 'locales/en';
import { showErrorMessage } from '../notification';
import { getStoreList, storeListSelector } from '../javelinStore';
import { logEvent } from 'utils/ga';

const companyAdapter = createEntityAdapter({
  sortComparer: (a, b) => a.name.localeCompare(b.name),
});

export const initialState = companyAdapter.getInitialState({
  loading: false,
  current: null,
  merchant_category_codes: [],
  clients: [],
  hop_error: {},
  searchResult: [],
  isSearching: false,
  hopRedirecting: false,
  pagination: {
    total: 1,
    total_pages: 1,
    current_page: 1,
    per_page: 25,
    count: 1,
  },
  params: {
    per_page: 25,
  },
  search: {
    term: '',
  },
});

export const getCompanyList = createAsyncThunk(
  COMPANY_LIST,
  async ({ term = null }, { rejectWithValue, dispatch, signal }) => {
    try {
      const source = axios.CancelToken.source();
      signal.addEventListener('abort', () => {
        source.cancel();
      });

      const { data, meta } = await GET_COMPANY_LIST(term, source.token);

      return { data, meta };
    } catch (error) {
      return rejectWithValue(rejectValue(error.response));
    }
  }
);

export const getCompanies = createAsyncThunk(
  COMPANY_ALL,
  async (_, { rejectWithValue, getState }) => {
    try {
      let params = { ...getState().company.params };

      const { data, meta } = await GET_COMPANIES({
        ...params,
      });

      if (!data.length) return { data: [], pagination: meta.pagination };

      const _data = data.map(flatCompany);

      return { data: _data, pagination: meta.pagination };
    } catch (error) {
      return rejectWithValue(rejectValue(error.response));
    }
  }
);

export const searchCompanies = createAsyncThunk(
  COMPANY_SEARCH,
  async (_, { rejectWithValue, getState }) => {
    try {
      const { term: searchTerm } = getState().company.search;

      if (!searchTerm) {
        return;
      }

      const { data, meta } = await GET_COMPANIES({
        term: searchTerm,
      });

      if (!data.length) return { data: [], pagination: meta.pagination };

      const _data = data.map(flatCompany);
      return { data: _data, pagination: meta.pagination };
    } catch (error) {
      return rejectWithValue(rejectValue(error.response));
    }
  }
);

/**
 * Selects the last company id the user has selected in the current browser session
 */
export const getCompanyOnInit = createAsyncThunk(
  COMPANY_ALL_INIT,
  async (_, { dispatch, rejectWithValue, getState }) => {
    try {
      const { payload } = await dispatch(getCompanyList({}));
      const { data, meta } = payload;

      const hasCompany =
        !!data.length && (meta.selected_company_id || data.length === 1);
      if (hasCompany) {
        await dispatch(
          selectCompany({
            companyId: meta.selected_company_id ?? data[0].id,
            skipGetStore: true,
          })
        );
      }
      return true;
    } catch (error) {
      return rejectWithValue(rejectValue(error.response));
    }
  }
);

export const getClients = createAsyncThunk(
  CLIENT_ALL,
  async (_, { dispatch, rejectWithValue, getState }) => {
    try {
      const { data } = await GET_CLIENTS();

      return data;
    } catch (error) {
      return rejectWithValue(rejectValue(error.response));
    }
  }
);

export const getUserClients = createAsyncThunk(
  USER_CLIENTS,
  async (_, { dispatch, rejectWithValue, getState }) => {
    try {
      const { data } = await GET_USER_CLIENTS();

      return data;
    } catch (error) {
      return rejectWithValue(rejectValue(error.response));
    }
  }
);

export const getMerchantCategoryCode = createAsyncThunk(
  MERCHANT_CATEGORY_CODE,
  async (_, { dispatch, rejectWithValue, getState }) => {
    try {
      const { data } = await GET_MERCHANT_CATEGORY_CODE();

      return data;
    } catch (error) {
      return rejectWithValue(rejectValue(error.response));
    }
  }
);

export const getCompanyById = createAsyncThunk(
  COMPANY_GET_BY_ID,
  async (company_id, { rejectWithValue }) => {
    try {
      const { data } = await GET_COMPANY_BY_ID(company_id);

      return data;
    } catch (error) {
      return rejectWithValue(rejectValue(error.response));
    }
  }
);

export const createCompanyAndStore = createAsyncThunk(
  CREATE_COMPANY_AND_STORE,
  async (payload, { dispatch, rejectWithValue, getState }) => {
    try {
      if (!getState().company.clients.length) {
        return dispatch(showErrorMessage('Unable to find client'));
      }
      if (payload.legal_entity !== TEXT.types.individual) {
        delete payload.first_name;
        delete payload.last_name;
      }
      if (payload.legal_entity === TEXT.types.individual) {
        delete payload.legal_business_name;
      }

      const { name, country_name, country_code, ..._companyPayload } = payload;
      const companyPayload = {
        ..._companyPayload,
        client_id: getState().company.clients[0]?.id,
        country: country_code,
      };
      const storePayload = {
        name,
        country: country_name,
      };

      const { data } = await POST_COMPANY(companyPayload, storePayload);
      if (data.company_id)
        await dispatch(selectCompany({ companyId: data.company_id }));
      return data;
    } catch (error) {
      return rejectWithValue(rejectValue(error.response));
    }
  }
);

export const createCompany = createAsyncThunk(
  COMPANY_CREATE_ACTION,
  async (payload, { rejectWithValue }) => {
    try {
      await POST_COMPANY_CREATE(payload);
    } catch (error) {
      return rejectWithValue(rejectValue(error.response));
    }
  }
);

export const generateHopUrl = createAsyncThunk(
  GENERATE_HOP_URL,
  async (
    { displayRedirection = false },
    { dispatch, rejectWithValue, getState }
  ) => {
    const companyId = getState().company.current;
    if (!companyId) return {};

    try {
      const data = await POST_HOP_URL(companyId);
      return data;
    } catch (error) {
      return rejectWithValue(rejectValue(error.response));
    }
  }
);

export const updateKycStatus = createAsyncThunk(
  UPDATE_KYC_STATUS,
  async (company_id, { rejectWithValue }) => {
    try {
      await POST_UPDATE_KYC_STATUS(company_id);
      return {
        id: company_id,
        changes: {
          account_holder_kyc_just_approved: false,
        },
      };
    } catch (error) {
      return rejectWithValue(rejectValue(error.response));
    }
  }
);
export const selectCompany = createAsyncThunk(
  COMPANY_SELECT,
  async (
    { skipGetStore, companyId },
    { dispatch, rejectWithValue, getState }
  ) => {
    try {
      const { data } = await POST_COMPANY_SELECT(companyId);
      if (data.id !== getState().company.current && !skipGetStore) {
        dispatch(getStoreList());
      }
      return data;
    } catch (error) {
      return rejectWithValue(rejectValue(error.response));
    }
  }
);

const companySlice = createSlice({
  name: COMPANY,
  initialState,
  reducers: {
    setParams: (state, { payload }) => {
      state.params = { ...state.params, ...payload };
    },
    setSearchFilter: (state, { payload }) => {
      state.search.filter = payload;
    },
    setSearchTerm: (state, { payload }) => {
      state.search.term = payload;
    },
    resetSearch: (state) => {
      state.search = initialState.search;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(updateKycStatus.fulfilled, companyAdapter.updateOne)
      .addCase(generateHopUrl.pending, (state, action) => {
        state.hopRedirecting = action.meta.arg.displayRedirection;
      })
      .addCase(generateHopUrl.rejected, (state, action) => {
        state.hop_error = action?.payload;
      })
      .addCase(generateHopUrl.fulfilled, (state) => {
        state.hop_error = {};
      })
      .addCase(getCompanies.pending, (state) => {
        state.loading = true;
        state.search.submitted = false;
      })
      .addCase(getCompanies.rejected, (state) => {
        state.loading = false;
      })
      .addCase(getCompanies.fulfilled, (state, { payload }) => {
        state.loading = false;
        companyAdapter.setAll(state, payload.data);
        state.pagination = payload.pagination;
      })
      .addCase(searchCompanies.pending, (state) => {
        state.isLoading = true;
        state.search.submitted = true;
      })
      .addCase(searchCompanies.rejected, (state) => {
        state.isLoading = false;
        state.search.submitted = false;
      })
      .addCase(searchCompanies.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        companyAdapter.setAll(state, payload.data);
        state.pagination = payload.pagination;
      })
      .addCase(getCompanyList.pending, (state, { meta }) => {
        if (meta.arg.term) {
          state.isSearching = true;
        } else {
          state.loading = true;
        }
      })
      .addCase(getCompanyList.fulfilled, (state, { payload, meta }) => {
        if (meta.arg.term) {
          state.isSearching = false;
          state.searchResult = payload.data;
        } else {
          state.loading = false;
          companyAdapter.setAll(state, payload.data);
          state.pagination = payload.meta.pagination;
        }
      })
      .addCase(getCompanyList.rejected, (state, { meta }) => {
        if (meta?.arg?.term && !meta?.aborted) {
          state.isSearching = false;
        } else {
          state.loading = false;
        }
      })
      .addCase(getCompanyById.rejected, (state) => {
        state.loading = false;
      })
      .addCase(getCompanyById.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(getCompanyById.fulfilled, (state, action) => {
        state.loading = false;
        companyAdapter.upsertOne(state, action.payload);
      })
      .addCase(createCompanyAndStore.fulfilled, (state, { payload }) => {
        if (payload.company_id) {
          logEvent('onboarding_complete', { company_id: payload.company_id });
        }
      })
      .addCase(createCompanyAndStore.rejected, (state, { payload }) => {
        logEvent('onboarding_fail', { message: payload });
      })
      .addCase(getClients.fulfilled, (state, { payload }) => {
        state.clients = payload;
      })
      .addCase(getUserClients.fulfilled, (state, { payload }) => {
        state.clients = payload;
      })
      .addCase(getMerchantCategoryCode.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.merchant_category_codes = payload;
      })
      .addCase(selectCompany.fulfilled, (state, action) => {
        if (action.payload.id) {
          state.current = action.payload.id;
          companyAdapter.upsertOne(state, action.payload);
        }
      })
      .addDefaultCase((state) => state);
  },
});

export const { setParams, setSearchTerm, resetSearch } = companySlice.actions;

export const companySelector = companyAdapter.getSelectors(
  (state) => state.company
);

export const companyAllSelector = (state) => ({
  data: companySelector.selectAll(state),
  pagination: state.company.pagination,
  isLoading: state.company.loading,
  params: state.company.params,
  searchTerm: state.company.search.term,
});

export const currentCompanySelector = createDraftSafeSelector(
  (state) => state,
  (state) => companySelector.selectById(state, state.company.current) || {}
);

/**
 * Returns the company name or the stripe account id
 * when no company name is given
 * @param {object} company
 * @returns {string} company identifier
 */
const getCompanyIdentifier = (company) => {
  if (!company) {
    return null;
  }
  return company.name.trim() === '' ? company.stripe_account_id : company.name;
};

export const companyAndStoreSelector = createDraftSafeSelector(
  (state) => state,
  (state) => {
    const { isSearching, searchResult } = state.company;
    const _companies = companySelector.selectAll(state);

    const companyList = !!_companies.length
      ? _companies.map((company) => {
          const name = getCompanyIdentifier(company);

          return {
            id: company.id,
            name,
          };
        })
      : [];

    const _stores = storeListSelector.selectAll(state);
    const storeList = !!_stores.length
      ? _stores.map((store) => ({
          id: store.id,
          name: store.name,
        }))
      : [];
    const _currentStore = storeListSelector.selectById(
      state,
      state.javelinStore.current
    );
    const currentStore = _currentStore
      ? {
          id: _currentStore.id,
          name: _currentStore.name,
        }
      : {};
    const _currentCompany = companySelector.selectById(
      state,
      state.company.current
    );

    const currentCompanyName = getCompanyIdentifier(_currentCompany);
    const currentCompany = _currentCompany
      ? {
          id: _currentCompany.id,
          name: currentCompanyName,
        }
      : {};
    return {
      isSearching,
      companyList,
      currentCompany,
      searchResult,
      storeList,
      currentStore,
    };
  }
);

export default companySlice.reducer;
